import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { Calendar, Clock, Smartphone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MyBookingsPage = () => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBookings = async () => {
            try {
                const response = await axios.get(`${API}/api/my-bookings`, {
                });
                setBookings(response.data);
            } catch {
                toast.error("Errore nel caricamento delle prenotazioni");
            } finally {
                setLoading(false);
            }
        };
        fetchBookings();
    }, []);

    const getStatusBadge = (status) => {
        const styles = {
            confirmed: 'badge-success',
            pending: 'badge-warning',
            completed: 'badge-primary',
            cancelled: 'badge-danger'
        };
        const labels = {
            confirmed: 'Confermato',
            pending: 'In attesa',
            completed: 'Completato',
            cancelled: 'Annullato'
        };
        return <span className={`badge ${styles[status] || 'badge-muted'}`}>{labels[status] || status}</span>;
    };

    if (loading) {
        return (
            <div className="min-h-screen py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-3xl font-bold mb-8">Le mie prenotazioni</h1>
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-surface rounded-xl h-32 animate-pulse" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold mb-8" data-testid="bookings-title">Le mie prenotazioni</h1>

                {bookings.length === 0 ? (
                    <div className="text-center py-16 bg-surface rounded-xl border border-border">
                        <Calendar className="w-16 h-16 mx-auto text-muted mb-4" />
                        <h2 className="text-xl font-semibold mb-2">Nessuna prenotazione</h2>
                        <p className="text-muted">Non hai ancora prenotato una riparazione</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {bookings.map((booking) => (
                            <div
                                key={booking.id}
                                className="bg-surface border border-border rounded-xl p-6"
                                data-testid={`booking-${booking.id}`}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                                    <div>
                                        <h3 className="font-semibold text-lg">{booking.service_name}</h3>
                                        <p className="text-2xl font-bold text-primary">€{booking.service_price.toFixed(2)}</p>
                                    </div>
                                    {getStatusBadge(booking.status)}
                                </div>

                                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2 text-muted">
                                        <Calendar className="w-4 h-4 text-primary" />
                                        <span>{format(parseISO(booking.date), 'dd MMMM yyyy', { locale: it })}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-muted">
                                        <Clock className="w-4 h-4 text-primary" />
                                        <span>{booking.time_slot}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-muted">
                                        <Smartphone className="w-4 h-4 text-primary" />
                                        <span>{booking.device_model}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-muted">
                                        <MapPin className="w-4 h-4 text-primary" />
                                        <span>Via Giuseppe Atzori 44/6, Nocera Inferiore</span>
                                    </div>
                                </div>

                                {booking.notes && (
                                    <div className="mt-4 p-3 bg-surface-elevated rounded-lg">
                                        <p className="text-sm text-muted">{booking.notes}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyBookingsPage;
